import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Country, UserPrivilege } from "../../../../../../shared/constants/Constant";
import { useAppUser } from "../../../../../../contexts/user-context/UserContext";
import { useFirestore, useFunctions, useStorage } from "reactfire";
import "./styles.scss";
import { useHistory } from "react-router-dom";
import { DashboardPages } from "../../..";
import DatePicker from "react-datepicker";
import FormValidator from "../../../../../../shared/utils/FormValidator";
import ProfilePageTitle from "../../containers/profileHeader";
import defautUserImage from "../../../../../../shared/resources/generic-user-profile-image.png";
import { FaPencilAlt } from "react-icons/fa";
import random from "randomstring";
import ConfirmationModal from "../../../../../../shared/components/confirmationModal/ConfirmationModal";
import ForumUtils, {
  ForumUpdateUser,
} from "../../../../../../shared/utils/ForumUtils";
import { usePermission } from "../../../../../../shared/hooks/usePermission";

interface User {
  userId: string | undefined;
  fullName: string | undefined;
  name?:string;
  email: string | null | undefined;
  userRole: string | undefined;
  country?: Country;
  username: string | undefined;
  dob: Date | null;
  university: string;
  contactNo: string;
  bio: string;
}

const initUser: User = {
  userId: "",
  fullName: "",
  email: "",
  userRole: "",
  country: Country.SRI_LANKA,
  username: "",
  dob: null,
  university: "",
  contactNo: "",
  bio: "",
};

interface Errors {
  fullNameError: string;
  dobError: string;
  universityError: string;
  usernameError: string;
  contactNoError: string;
  bioError: string;
  emailError: string;
  userRoleError: string;
}

const initErrors: Errors = {
  fullNameError: "",
  dobError: "",
  universityError: "",
  usernameError: "",
  contactNoError: "",
  bioError: "",
  emailError: "",
  userRoleError: "",
};

interface State {
  showAcceptConfirmation: boolean;
}

const initState: State = {
  showAcceptConfirmation: false,
};

const UserProfileEditComponent = () => {
  const appUser = useAppUser();
  const history = useHistory();
  const store = useFirestore();

  const [user, setUser] = useState(initUser);
  const [errorState, setErrorState] = useState(initErrors);
  const [state, setState] = useState(initState);
  const [loading, setLoading] = useState(false);
  const [fileInput, setFileInput] = useState<any>(null);
  const [userImage, setUserImage] = useState<{
    file: File | null;
    preview: any;
  }>({ file: null, preview: null });

  const isTeamMember = usePermission(UserPrivilege.TEAM);

  const updateUserProfileRef = useFunctions().httpsCallable(
    "updateUserProfile"
  );

  useEffect(() => {
    if (
      localStorage.getItem("isAuthed") === "true" ||
      sessionStorage.getItem("isAuthed") === "true"
    ) {
      setUser({
        userId: appUser.fireUser?.uid,
        fullName: appUser.displayName,
        email: appUser.fireUser?.email,
        userRole: appUser.userRole.isAdmin
          ? "Admin"
          : appUser.userRole.isModerator
          ? "Moderator"
          : isTeamMember ? "Team Member" : "Member",
        country: appUser.country,
        username: appUser.username,
        dob:
          appUser.dob && !FormValidator.areDatesEqual(appUser.dob, new Date())
            ? appUser.dob
            : null,
        university: appUser.university,
        contactNo: appUser.contactNo,
        bio: appUser.bio,
      });
      // setUserImage({file: null, preview: appUser.fireUser?.photoURL});
    }
  }, [appUser, isTeamMember]);

  useEffect(() => {
    if (appUser.fireUser) {
      const unset1 = store
        .collection("users")
        .where("uid", "==", appUser.fireUser?.uid)
        .onSnapshot({}, (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            if (change.type === "added" || change.type === "modified") {
              setUserImage({
                file: null,
                preview: change.doc.data()?.photoURL,
              });
            }
            if (change.type === "removed") {
              setUserImage({ file: null, preview: defautUserImage });
            }
          });
        });

      return () => {
        unset1();
      };
    }
  }, [store, appUser.fireUser]);

  const validateData = (): boolean => {
    const fnValidation = FormValidator.validateString(
      user.fullName,
      "Full Name",
      3,
      100
    );

    const unValidation = FormValidator.validateString(
      user.username,
      "Username",
      3,
      100
    );

    const bioValidation =
      user.bio === undefined || user.bio === ""
        ? { message: "", isValid: true }
        : FormValidator.validateString(user.bio, "Bio", 3, 500);

    const dobValidation =
      user.dob === null
        ? {
            message: "",
            isValid: true,
          }
        : FormValidator.validateDobFromAccount(
            user.dob || undefined,
            "Date of birth"
          );

    const cnValidation =
      user.contactNo === undefined || user.contactNo === ""
        ? {
            message: "",
            isValid: true,
          }
        : appUser.country !== Country.JAMAICA
        ? FormValidator.validatePhoneNumberFromAccount(user.contactNo)
        : FormValidator.validateJamaicaPhoneNumberFromAccount(user.contactNo);

    setErrorState({
      ...errorState,
      fullNameError: fnValidation.message,
      dobError: dobValidation.message,
      usernameError: unValidation.message,
      contactNoError: cnValidation.message,
      bioError: bioValidation.message,
    });

    return (
      fnValidation.isValid &&
      dobValidation.isValid &&
      unValidation.isValid &&
      cnValidation.isValid &&
      bioValidation.isValid
    );
  };

  const storage = useStorage().ref();

  const saveAccountData = () => {
    setLoading(true);
    if (userImage.file) {
      // add loading
      const uploadTask = storage
        .child(
          `profilePictures/${random.generate(10)}.${userImage?.file.name
            .split(".")
            .pop()
            ?.toLowerCase()}`
        )
        .put(userImage.file);
      uploadTask.on("state_changed", function(snapshot) {
        let progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        console.log("pro pic progress ", progress);
      });
      uploadTask.then((snapshot) => {
        snapshot.ref.getDownloadURL().then((url) => {
          const userData = {
            uid: appUser.fireUser?.uid,
            displayName: user.fullName,
            email: user.email,
            username: user.username,
            dob: user.dob ? user.dob.toDateString() : undefined,
            university: user.university || "",
            phoneNumber: user.contactNo || "",
            bio: user.bio || "",
            photoURL: url || "",
          };

          updateUserProfileRef(userData)
            .then((data) => {
              setLoading(false);
              setState({
                ...state,
                showAcceptConfirmation: false,
              });
              updateForumData({
                email: user.email || "",
                password: appUser.forumPassword || "",
                displayName: user.fullName,
                profileImage: url,
                id: "",
                bio: user.bio,
              });
              history.push(DashboardPages.ACCOUNT);
            })
            .catch((error) => {
              setLoading(false);
              setState({
                ...state,
                showAcceptConfirmation: false,
              });
              const errorMessage: string = error.message;
              console.log("Error Message = ", errorMessage);
            });
        });
      });

      uploadTask.catch((error) => {
        setLoading(false);
        setState({
          ...state,
          showAcceptConfirmation: false,
        });
        const errorMessage: string = error.message;
        console.log("Error Message = ", errorMessage);
      });
    } else {
      const userData = {
        uid: appUser.fireUser?.uid,
        photoURL: userImage.preview,
        displayName: user.fullName,
        email: user.email,
        username: user.username,
        dob: user.dob ? user.dob.toDateString() : undefined,
        university: user.university || "",
        phoneNumber: user.contactNo || "",
        bio: user.bio || "",
      };

      updateUserProfileRef(userData)
        .then((data) => {
          setLoading(false);
          setState({
            ...state,
            showAcceptConfirmation: false,
          });
          console.log("running 1");
          updateForumData({
            email: user.email || "",
            password: appUser.forumPassword || "",
            displayName: user.fullName,
            id: "",
            profileImage: appUser.photoURL,
            bio: user.bio,
          });
          history.push(DashboardPages.ACCOUNT);
        })
        .catch((error) => {
          setLoading(false);
          setState({
            ...state,
            showAcceptConfirmation: false,
          });
          const errorMessage: string = error.message;
          console.log("Error Message = ", errorMessage);
        });
    }
  };

  const updateForumData = (payload: ForumUpdateUser) => {
    console.log("running 2");
    ForumUtils.updateUser(
      payload,
      () => {
        console.log("forum update ok");
      },
      () => {
        console.log("forum update not ok");
      }
    );
  };

  const buttonSet = (
    <Row>
      <Col xs={12} className="text-right mb-3">
        <div
          className="cancel-button blue"
          style={{ marginRight: "15px" }}
          onClick={() => {
            history.push(DashboardPages.ACCOUNT);
          }}
        >
          Cancel
        </div>
        <div
          className="save-button"
          style={{ marginLeft: "15px" }}
          onClick={() => {
            validateData() &&
              setState({ ...state, showAcceptConfirmation: true });
          }}
        >
          Save
        </div>
      </Col>
    </Row>
  );

  return (
    <>
      <ProfilePageTitle />
      <Container
        fluid
        style={{
          paddingTop: "200px",
          paddingLeft: "50px",
          paddingRight: "50px",
          color: "grey",
        }}
      >
        <Row>
          <Col xs={12} xl={7} lg={7} style={{ paddingRight: "3rem" }}>
            <Row>
              <div
                className="mt-3 d-flex justify-content-center p-0 mb-3"
                style={{ flex: 1 }}
              >
                <div className="d-flex justify-content-center">
                  <div
                    className="profile-image-pv"
                    onClick={() => fileInput?.click()}
                  >
                    <div className="shade d-flex justify-content-center align-items-start pt-2">
                      <FaPencilAlt />
                    </div>
                    <img
                      src={
                        userImage.preview ? userImage.preview : defautUserImage
                      }
                      alt=""
                    />
                  </div>
                </div>

                <input
                  ref={(input) => setFileInput(input)}
                  className="d-none"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      let reader = new FileReader();
                      let file = files[0];
                      reader.onloadend = () => {
                        setUserImage({
                          preview: reader.result,
                          file: file,
                        });
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
              </div>
            </Row>
          </Col>
          <Col xs={12} xl={5} lg={5} style={{ paddingRight: "3rem" }}>
            {buttonSet}
          </Col>
        </Row>
        <Row>
          <Col xs={12} xl={7} lg={7} style={{ paddingRight: "3rem" }}>
            <Row>
              <div
                className="mt-3 p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>
                    Full Name{" "}
                    <span className="ml-1" style={{ color: "red" }}>
                      *
                    </span>
                  </strong>
                </p>
              </div>
              <div
                className="mt-3 d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{ marginLeft: "10px", lineHeight: "27px", flex: 1 }}
                >
                  <div className="w-100">
                    <input
                      value={user.fullName}
                      onChange={(e) => {
                        setUser({ ...user, fullName: e.target.value });
                        setErrorState({ ...errorState, fullNameError: "" });
                      }}
                      className={
                        errorState.fullNameError.length < 1
                          ? "app-input"
                          : "app-input-profile invalid"
                      }
                    />
                    <label className="error-message">
                      {errorState.fullNameError}
                    </label>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>Email</strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{ marginLeft: "10px", lineHeight: "27px", flex: 1 }}
                >
                  <div className="w-100">
                    <input
                      disabled={true}
                      value={user.email || ""}
                      className={
                        errorState.emailError.length < 1
                          ? "app-input disabled"
                          : "app-input-profile invalid"
                      }
                    />
                    <label className="error-message"></label>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>Date of Birth</strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{ marginLeft: "10px", lineHeight: "27px", flex: 1 }}
                >
                  <div className="w-100">
                    <DatePicker
                      className={
                        errorState.dobError.length < 1
                          ? "app-input calender-img"
                          : "app-input-profile invalid calender-img"
                      }
                      onChange={(date: Date) => {
                        setUser({ ...user, dob: date });
                        setErrorState({ ...errorState, dobError: "" });
                      }}
                      selected={user.dob && user.dob}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      dateFormat="dd/MM/yyyy"
                      placeholderText={"DD/MM/YYYY"}
                      popperClassName={"customDatePicker"}
                      customInput={<input className="app-input-profile" />}
                    />
                    <div>
                      <label className="error-message">
                        {errorState.dobError}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>University / Institute</strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{
                    marginLeft: "10px",
                    lineHeight: "27px",
                    flex: 1,
                  }}
                >
                  <div className="w-100">
                    <input
                      value={user.university}
                      onChange={(e) => {
                        setUser({
                          ...user,
                          university: e.target.value,
                        });
                        setErrorState({
                          ...errorState,
                          universityError: "",
                        });
                      }}
                      className={
                        errorState.universityError.length < 1
                          ? "app-input"
                          : "app-input-profile invalid"
                      }
                    />
                    <label className="error-message">
                      {errorState.universityError}
                    </label>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>
                    Username{" "}
                    <span className="ml-1" style={{ color: "red" }}>
                      *
                    </span>
                  </strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{
                    marginLeft: "10px",
                    lineHeight: "27px",
                    flex: 1,
                  }}
                >
                  <div className="w-100">
                    <input
                      value={user.username || ""}
                      onChange={(e) => {
                        setUser({ ...user, username: e.target.value });
                        setErrorState({ ...errorState, usernameError: "" });
                      }}
                      className={
                        errorState.usernameError.length < 1
                          ? "app-input"
                          : "app-input-profile invalid"
                      }
                    />
                    <div>
                      <label className="error-message">
                        {errorState.usernameError}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>Phone No. </strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{
                    marginLeft: "10px",
                    lineHeight: "27px",
                    flex: 1,
                  }}
                >
                  <div className="w-100">
                    <input
                      value={user.contactNo}
                      onChange={(e) => {
                        setUser({ ...user, contactNo: e.target.value });
                        setErrorState({ ...errorState, contactNoError: "" });
                      }}
                      className={
                        errorState.contactNoError.length < 1
                          ? "app-input"
                          : "app-input-profile invalid"
                      }
                    />
                    <div>
                      <label className="error-message">
                        {!appUser.userRole.isModerator &&
                          errorState.contactNoError}
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </Row>
            <Row>
              <div
                className="p-0 pl-2"
                style={{ minWidth: "170px", lineHeight: "40px" }}
              >
                <p>
                  <strong>User Role</strong>
                </p>
              </div>
              <div
                className="d-flex align-content-center p-0"
                style={{ flex: 1 }}
              >
                <div
                  className="d-flex align-content-center"
                  style={{ lineHeight: "40px" }}
                >
                  <strong> : </strong>
                </div>
                <div
                  className="d-flex align-content-center"
                  style={{
                    marginLeft: "10px",
                    lineHeight: "27px",
                    flex: 1,
                  }}
                >
                  <div className="w-100">
                    <input
                      disabled={true}
                      value={user.userRole}
                      className={
                        errorState.userRoleError.length < 1
                          ? "app-input disabled"
                          : "app-input-profile invalid"
                      }
                    />
                    <div>
                      <label className="error-message"></label>
                    </div>
                  </div>
                </div>
              </div>
            </Row>
          </Col>
          <Col
            xs={12}
            xl={5}
            lg={5}
            className="pt-2 pl-1"
            style={{ transform: "translateY(-140px)" }}
          >
            <Row>
              <div className="mt-2" style={{ width: "50px" }}>
                <p>
                  <strong>Bio :</strong>
                </p>
              </div>
              <div className="mt-2" style={{ flex: 1 }}>
                <textarea
                  value={user.bio}
                  onChange={(e) => {
                    setUser({ ...user, bio: e.target.value });
                    setErrorState({ ...errorState, bioError: "" });
                  }}
                  className={
                    errorState.bioError.length < 1
                      ? "app-input-bio"
                      : "app-input-bio invalid"
                  }
                />
                <div>
                  <label className="error-message">{errorState.bioError}</label>
                </div>
              </div>
            </Row>
          </Col>
        </Row>
        {state.showAcceptConfirmation && (
          <ConfirmationModal
            loading={loading}
            children={{
              headerText: "Confirm Updates",
              bodyText: <>Are you sure you want to update the content?</>,
              cancelButtonText: "Cancel",
              confirmButtonText: "Update",
              confirmButtonColor: loading ? "grayStatic" : "blue",
            }}
            show={state.showAcceptConfirmation}
            onCancel={() =>
              setState({
                ...state,
                showAcceptConfirmation: false,
              })
            }
            onConfirm={() => {
              !loading && saveAccountData();
            }}
          />
        )}
      </Container>
    </>
  );
};

export default UserProfileEditComponent;
